import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CustomGameInput,
  GroupCreate,
  GroupUpdate,
  ManagedMemberCreate,
  PlayCreate,
  PlayUpdate,
  SeasonCreate,
  SeasonUpdate,
} from '@topmeeps/shared'
import { api } from './client'
import { ApiError } from './client'
import type {
  BggSearchResponse,
  CreatePlayResponse,
  DashboardActiveSeason,
  DashboardRecentPlay,
  DuplicateCandidate,
  Friends,
  GameDetail,
  GameRow,
  GroupInvite,
  GroupListItem,
  GroupMemberWithBlock,
  Guest,
  InviteAcceptResponse,
  InvitePreview,
  LibraryEntry,
  Me,
  OraclePrediction,
  PlayDetail,
  PlayListItem,
  Profile,
  PublicProfile,
  PublicSeasonSummary,
  SeasonDetail,
  SeasonRow,
  ShareTokenResponse,
  StandingsByGame,
} from './types'

export const useMe = () =>
  useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/me'),
    staleTime: 5 * 60_000,
  })

export const useMyProfile = () =>
  useQuery({
    queryKey: ['me', 'profile'],
    queryFn: () => api.get<Profile>('/me/profile'),
  })

export const useChangeHandle = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (handle: string) => api.patch<Me>('/me/handle', { handle }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] })
      qc.invalidateQueries({ queryKey: ['user'] })
      qc.invalidateQueries({ queryKey: ['friends'] })
      qc.invalidateQueries({ queryKey: ['group'] })
    },
  })
}

export const useSignIn = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { handle: string; code: string }) => api.post<Me>('/auth/sign-in', input),
    onSuccess: () => qc.invalidateQueries(),
  })
}

export const useSignOut = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>('/auth/sign-out', {}),
    onSuccess: () => {
      qc.clear()
    },
  })
}

export const useDeleteAccount = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.del<{ ok: true }>('/me/account'),
    onSuccess: () => {
      qc.clear()
    },
  })
}

export const useOraclePredict = (
  gameId: string,
  groupId: string | null,
  userIds: ReadonlyArray<string>,
) => {
  const sortedKey = [...userIds].sort().join(',')
  return useQuery({
    queryKey: ['oracle', gameId, groupId, sortedKey],
    queryFn: () =>
      api.post<OraclePrediction>('/oracle/predict', {
        gameId,
        groupId,
        userIds: [...userIds],
      }),
    enabled: userIds.length >= 2,
    staleTime: 30_000,
  })
}

export const useRecentPlays = () =>
  useQuery({
    queryKey: ['me', 'recent-plays'],
    queryFn: () => api.get<DashboardRecentPlay[]>('/me/recent-plays'),
  })

export const useActiveSeasons = () =>
  useQuery({
    queryKey: ['me', 'active-seasons'],
    queryFn: () => api.get<DashboardActiveSeason[]>('/me/active-seasons'),
  })

export const useGroups = () =>
  useQuery({
    queryKey: ['groups'],
    queryFn: () => api.get<GroupListItem[]>('/groups'),
  })

export const useGroup = (id: string) =>
  useQuery({
    queryKey: ['group', id],
    queryFn: () => api.get<GroupListItem>(`/groups/${id}`),
    enabled: Boolean(id),
  })

export const useGroupMembers = (id: string) =>
  useQuery({
    queryKey: ['group', id, 'members'],
    queryFn: () => api.get<GroupMemberWithBlock[]>(`/groups/${id}/members`),
    enabled: Boolean(id),
  })

export const useGames = () =>
  useQuery({
    queryKey: ['games'],
    queryFn: () => api.get<GameRow[]>('/games'),
  })

export const useBggSearch = (query: string) =>
  useQuery({
    queryKey: ['bgg', 'search', query],
    queryFn: () => api.get<BggSearchResponse>(`/bgg/search?q=${encodeURIComponent(query)}`),
    enabled: query.trim().length >= 2,
    staleTime: 60 * 60_000,
  })

export const useImportBggGame = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (bggId: number) => api.post<GameRow>('/games/import-bgg', { bggId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['games'] })
      qc.invalidateQueries({ queryKey: ['group'] })
    },
  })
}

export const useCreateCustomGame = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CustomGameInput) => api.post<GameRow>('/games/custom', input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['games'] })
      qc.invalidateQueries({ queryKey: ['group', vars.groupId, 'games'] })
    },
  })
}

export const useFriends = () =>
  useQuery({
    queryKey: ['friends'],
    queryFn: () => api.get<Friends>('/friends'),
  })

export const useSendFriendRequest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (handle: string) =>
      api.post<{ resultStatus: 'requested' | 'accepted'; otherUserId: string }>(
        '/friends/request',
        { handle },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['friends'] })
      qc.invalidateQueries({ queryKey: ['user'] })
    },
  })
}

export const useFriendAction = () => {
  const qc = useQueryClient()
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['friends'] })
    qc.invalidateQueries({ queryKey: ['group'] })
    qc.invalidateQueries({ queryKey: ['user'] })
  }
  return {
    accept: useMutation({
      mutationFn: (userId: string) => api.post(`/friends/${userId}/accept`, {}),
      onSuccess: invalidate,
    }),
    decline: useMutation({
      mutationFn: (userId: string) => api.post(`/friends/${userId}/decline`, {}),
      onSuccess: invalidate,
    }),
    unfriend: useMutation({
      mutationFn: (userId: string) => api.del(`/friends/${userId}`),
      onSuccess: invalidate,
    }),
    block: useMutation({
      mutationFn: (userId: string) => api.post(`/friends/${userId}/block`, {}),
      onSuccess: invalidate,
    }),
    unblock: useMutation({
      mutationFn: (userId: string) => api.del(`/friends/${userId}/block`),
      onSuccess: invalidate,
    }),
  }
}

export const useGuests = () =>
  useQuery({
    queryKey: ['guests'],
    queryFn: () => api.get<Guest[]>('/guests'),
  })

export const useCreateGuest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; groupId: string | null }) =>
      api.post<Guest>('/guests', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['guests'] }),
  })
}

export const useRenameGuest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch<Guest>(`/guests/${id}`, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['guests'] }),
  })
}

export const useDeleteGuest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/guests/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['guests'] }),
  })
}

export const useGroupGames = (id: string) =>
  useQuery({
    queryKey: ['group', id, 'games'],
    queryFn: () => api.get<GameRow[]>(`/groups/${id}/games`),
    enabled: Boolean(id),
  })

export const useCreateGroup = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: GroupCreate) => api.post<GroupListItem>('/groups', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  })
}

export const useEditGroup = (groupId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: GroupUpdate) => api.patch<GroupListItem>(`/groups/${groupId}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['group', groupId] })
      qc.invalidateQueries({ queryKey: ['groups'] })
    },
  })
}

export const useDeleteGroup = (groupId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.del<{ ok: true }>(`/groups/${groupId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] })
      qc.removeQueries({ queryKey: ['group', groupId] })
    },
  })
}

export const useAddManagedMember = (groupId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: ManagedMemberCreate) =>
      api.post<GroupMemberWithBlock>(`/groups/${groupId}/members`, { kind: 'managed', ...input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', groupId, 'members'] }),
  })
}

export const useAddExistingMember = (groupId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (handle: string) =>
      api.post<GroupMemberWithBlock>(`/groups/${groupId}/members`, { kind: 'existing', handle }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', groupId, 'members'] }),
  })
}

export const useSetMemberRole = (groupId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'admin' | 'member' }) =>
      api.patch<null>(`/groups/${groupId}/members/${userId}`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', groupId, 'members'] }),
  })
}

export const useRemoveMember = (groupId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => api.del<null>(`/groups/${groupId}/members/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', groupId, 'members'] }),
  })
}

export const useLeaveGroup = (groupId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<null>(`/groups/${groupId}/leave`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] })
      qc.invalidateQueries({ queryKey: ['group', groupId] })
    },
  })
}

export const useGroupInvites = (groupId: string, enabled = true) =>
  useQuery({
    queryKey: ['group', groupId, 'invites'],
    queryFn: () => api.get<GroupInvite[]>(`/groups/${groupId}/invites`),
    enabled: Boolean(groupId) && enabled,
  })

export const useCreateInvite = (groupId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<GroupInvite>(`/groups/${groupId}/invites`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', groupId, 'invites'] }),
  })
}

export const useRevokeInvite = (groupId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (inviteId: string) =>
      api.del<{ ok: true; alreadyRevoked: boolean }>(`/invites/${inviteId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', groupId, 'invites'] }),
  })
}

export const useInvitePreview = (token: string) =>
  useQuery({
    queryKey: ['invite-preview', token],
    queryFn: () => api.get<InvitePreview>(`/public/invites/${token}`),
    enabled: Boolean(token),
    retry: (count, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 410)) return false
      return count < 2
    },
  })

export const useAcceptInvite = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) => api.post<InviteAcceptResponse>(`/invites/${token}/accept`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] })
    },
  })
}

export const useGameDetail = (groupId: string, gameId: string) =>
  useQuery({
    queryKey: ['group', groupId, 'game', gameId],
    queryFn: () => api.get<GameDetail>(`/groups/${groupId}/games/${gameId}`),
    enabled: Boolean(groupId && gameId),
  })

export const usePublicProfile = (handle: string) =>
  useQuery({
    queryKey: ['user', handle.toLowerCase()],
    queryFn: () => api.get<PublicProfile>(`/users/${encodeURIComponent(handle)}`),
    enabled: Boolean(handle),
  })

export const useGroupPlays = (id: string) =>
  useQuery({
    queryKey: ['group', id, 'plays'],
    queryFn: () => api.get<PlayListItem[]>(`/groups/${id}/plays`),
    enabled: Boolean(id),
  })

export const useGroupLibrary = (id: string) =>
  useQuery({
    queryKey: ['group', id, 'library'],
    queryFn: () => api.get<LibraryEntry[]>(`/groups/${id}/library`),
    enabled: Boolean(id),
  })

export const useGroupStandings = (id: string) =>
  useQuery({
    queryKey: ['group', id, 'standings'],
    queryFn: () => api.get<StandingsByGame[]>(`/groups/${id}/standings`),
    enabled: Boolean(id),
  })

export const usePlay = (id: string) =>
  useQuery({
    queryKey: ['play', id],
    queryFn: () => api.get<PlayDetail>(`/plays/${id}`),
    enabled: Boolean(id),
  })

export const useCreatePlay = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: PlayCreate) => api.post<CreatePlayResponse>('/plays', input),
    onSuccess: (_data, vars) => {
      if (vars.groupId) {
        qc.invalidateQueries({ queryKey: ['group', vars.groupId, 'plays'] })
        qc.invalidateQueries({ queryKey: ['group', vars.groupId, 'library'] })
        qc.invalidateQueries({ queryKey: ['group', vars.groupId, 'standings'] })
      }
      qc.invalidateQueries({ queryKey: ['me', 'profile'] })
    },
  })
}

export const usePatchPlay = (playId: string, groupId: string | null) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: PlayUpdate) =>
      api.patch<{ recomputedScopes: number }>(`/plays/${playId}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['play', playId] })
      if (groupId) {
        qc.invalidateQueries({ queryKey: ['group', groupId, 'plays'] })
        qc.invalidateQueries({ queryKey: ['group', groupId, 'library'] })
        qc.invalidateQueries({ queryKey: ['group', groupId, 'standings'] })
      }
      qc.invalidateQueries({ queryKey: ['me', 'profile'] })
    },
  })
}

export const usePlayDuplicates = (playId: string) =>
  useQuery({
    queryKey: ['play', playId, 'duplicates'],
    queryFn: () => api.get<DuplicateCandidate[]>(`/plays/${playId}/duplicates`),
    enabled: Boolean(playId),
  })

export const useDismissDuplicate = (playId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (otherId: string) =>
      api.post<null>(`/plays/${playId}/duplicates/${otherId}/dismiss`, {}),
    onSuccess: (_data, otherId) => {
      qc.invalidateQueries({ queryKey: ['play', playId, 'duplicates'] })
      qc.invalidateQueries({ queryKey: ['play', otherId, 'duplicates'] })
    },
  })
}

export const useMergeDuplicate = (canonicalId: string, groupId: string | null) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (duplicateId: string) =>
      api.post<{ recomputedScopes: number }>(
        `/plays/${canonicalId}/duplicates/${duplicateId}/merge`,
        {},
      ),
    onSuccess: (_data, duplicateId) => {
      qc.invalidateQueries({ queryKey: ['play', canonicalId] })
      qc.invalidateQueries({ queryKey: ['play', canonicalId, 'duplicates'] })
      qc.invalidateQueries({ queryKey: ['play', duplicateId] })
      if (groupId) {
        qc.invalidateQueries({ queryKey: ['group', groupId, 'plays'] })
        qc.invalidateQueries({ queryKey: ['group', groupId, 'standings'] })
        qc.invalidateQueries({ queryKey: ['group', groupId, 'library'] })
      }
      qc.invalidateQueries({ queryKey: ['me', 'recent-plays'] })
      qc.invalidateQueries({ queryKey: ['me', 'profile'] })
    },
  })
}

export const useDeletePlay = (groupId: string | null) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (playId: string) => api.del<{ recomputedScopes: number }>(`/plays/${playId}`),
    onSuccess: () => {
      if (groupId) {
        qc.invalidateQueries({ queryKey: ['group', groupId, 'plays'] })
        qc.invalidateQueries({ queryKey: ['group', groupId, 'library'] })
        qc.invalidateQueries({ queryKey: ['group', groupId, 'standings'] })
      }
      qc.invalidateQueries({ queryKey: ['me', 'profile'] })
    },
  })
}

export const useGroupSeasons = (groupId: string) =>
  useQuery({
    queryKey: ['group', groupId, 'seasons'],
    queryFn: () => api.get<SeasonRow[]>(`/groups/${groupId}/seasons`),
    enabled: Boolean(groupId),
  })

export const useSeason = (seasonId: string) =>
  useQuery({
    queryKey: ['season', seasonId],
    queryFn: () => api.get<SeasonDetail>(`/seasons/${seasonId}`),
    enabled: Boolean(seasonId),
  })

export const useCreateSeason = (groupId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SeasonCreate) => api.post<SeasonRow>(`/groups/${groupId}/seasons`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', groupId, 'seasons'] }),
  })
}

export const useEditSeason = (seasonId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SeasonUpdate) => api.patch<SeasonRow>(`/seasons/${seasonId}`, input),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['season', seasonId] })
      qc.invalidateQueries({ queryKey: ['group', row.groupId, 'seasons'] })
    },
  })
}

export const useDeleteSeason = (groupId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (seasonId: string) => api.del<{ ok: true }>(`/seasons/${seasonId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', groupId, 'seasons'] }),
  })
}

export const useGenerateShareToken = (seasonId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<ShareTokenResponse>(`/seasons/${seasonId}/share`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['season', seasonId] }),
  })
}

export const useRevokeShareToken = (seasonId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.del<{ ok: true; alreadyRevoked: boolean }>(`/seasons/${seasonId}/share`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['season', seasonId] }),
  })
}

export const usePublicSeason = (token: string) =>
  useQuery({
    queryKey: ['public-season', token],
    queryFn: () => api.get<PublicSeasonSummary>(`/public/season/${token}`),
    enabled: Boolean(token),
    retry: (count, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 410)) return false
      return count < 2
    },
  })
